import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertCircle, KeyRound, ShieldQuestion, Wallet, Wrench } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { NotAvailablePanel } from "../../components/desk/NotAvailablePanel";

// UX-FD-02 §1 SCR-P02-60/61/62. Petty cash is hardcoded to category="misc"+paid_from="cash" —
// the backend itself 403s anything else for a `expense.manage_petty`-only actor
// (`routers/expense.py`'s `petty_tier_only` check), so this is genuinely restricted, not just a
// UI default. Locker/Equipment panels only render if their module entitlement is actually on
// (`entitlements.lockers`/`entitlements.asset_issue`) — real module-gating, no fabricated
// fallback. Incident/complaint and a standalone "lost item" log have NO backend endpoint
// anywhere in this codebase — per the prompt's own "flag it, don't fabricate" instruction, this
// is shown as an honest "not connected yet" panel instead of a form that would silently do
// nothing.
export default function FloorOps() {
  const { branchId, entitlements } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [pettyAmount, setPettyAmount] = useState("");
  const [pettyNote, setPettyNote] = useState("");
  const [lockerKind, setLockerKind] = useState("");
  const [lockerPhone, setLockerPhone] = useState("");
  const [assetKind, setAssetKind] = useState("");
  const [assetPhone, setAssetPhone] = useState("");

  const resolveMemberRef = async (phone) => {
    if (!phone.trim()) return null;
    const res = await api.get("/members", { params: { phone: phone.trim() } });
    return res.data.members[0]?.id || null;
  };

  const pettyMutation = useMutation({
    mutationFn: () => api.post(
      "/expenses", { branch_id: branchId, category: "misc", amount: Math.round(Number(pettyAmount) * 100), paid_from: "cash", note: pettyNote || null },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => { toast.success("Petty-cash expense recorded."); setPettyAmount(""); setPettyNote(""); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const lockerMutation = useMutation({
    mutationFn: async () => api.post("/lockers/assign", { branch_id: branchId, kind: lockerKind, member_ref: await resolveMemberRef(lockerPhone) }),
    onSuccess: () => { toast.success("Locker assigned."); setLockerKind(""); setLockerPhone(""); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const assetMutation = useMutation({
    mutationFn: async () => api.post("/assets/issue", { branch_id: branchId, kind: assetKind, member_ref: await resolveMemberRef(assetPhone) }),
    onSuccess: () => { toast.success("Equipment issued."); setAssetKind(""); setAssetPhone(""); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.floorOpsPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Floor Ops</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Petty cash, lockers, equipment, and incident logging.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#141416" }}><Wallet size={15} /> Petty cash / misc (drawer cash only)</p>
        <div className="flex gap-2">
          <Input data-testid={DESK.pettyAmountInput} type="number" min={1} placeholder="Amount (₹)" value={pettyAmount} onChange={(e) => setPettyAmount(e.target.value)} />
          <Input data-testid={DESK.pettyNoteInput} placeholder="What for?" value={pettyNote} onChange={(e) => setPettyNote(e.target.value)} />
          <Button data-testid={DESK.pettySubmitButton} disabled={!pettyAmount || pettyMutation.isPending} onClick={() => pettyMutation.mutate()} className="text-white shrink-0" style={{ background: "#D6202B" }}>
            Record
          </Button>
        </div>
        {pettyAmount && <p className="text-xs" style={{ color: "#68686E" }}>{formatCurrency(Math.round(Number(pettyAmount) * 100))} from the drawer.</p>}
      </motion.div>

      {entitlements.lockers && (
        <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#141416" }}><KeyRound size={15} /> Assign a locker</p>
          <div className="flex gap-2">
            <Input data-testid={DESK.lockerKindInput} placeholder="Locker size/kind (e.g. small)" value={lockerKind} onChange={(e) => setLockerKind(e.target.value)} />
            <Input placeholder="Member phone (optional)" value={lockerPhone} onChange={(e) => setLockerPhone(e.target.value)} />
            <Button data-testid={DESK.lockerAssignButton} disabled={!lockerKind || lockerMutation.isPending} onClick={() => lockerMutation.mutate()} className="text-white shrink-0" style={{ background: "#141416" }}>
              Assign
            </Button>
          </div>
        </motion.div>
      )}

      {entitlements.asset_issue && (
        <motion.div {...fadeUp(reducedMotion, 0.09)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#141416" }}><Wrench size={15} /> Issue equipment</p>
          <div className="flex gap-2">
            <Input data-testid={DESK.assetKindInput} placeholder="Equipment (e.g. resistance band)" value={assetKind} onChange={(e) => setAssetKind(e.target.value)} />
            <Input placeholder="Member phone (optional)" value={assetPhone} onChange={(e) => setAssetPhone(e.target.value)} />
            <Button data-testid={DESK.assetIssueButton} disabled={!assetKind || assetMutation.isPending} onClick={() => assetMutation.mutate()} className="text-white shrink-0" style={{ background: "#141416" }}>
              Issue
            </Button>
          </div>
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.12)}>
        <NotAvailablePanel
          testId={DESK.incidentNotAvailable}
          title="Incident / lost item logging isn't wired up yet"
          reason="There's no backend record for incidents or standalone lost items yet — flagging this for the backend team rather than pretending to save something that wouldn't persist."
        />
      </motion.div>
    </div>
  );
}
